import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Card from "../components/Card";
import { ClientSection, ReachOutSection } from "../components/BottomSection";
import AdvertisingHeader from "../components/AdvertisingHeader";
import Clients from "../components/Clients";
import axios from "../lib/axios";
import Videos from "../components/Videos";
import MetaAdLibrarySection from "../components/MetaAdLibrarySection";
import { FaArrowDown } from "react-icons/fa";

const AdsPage = ({ categoryName }) => {
  const params = useParams();
  const rawCategory = categoryName || params.category;
  const category = (rawCategory || "")
    .toLowerCase()
    .trim()
    .replace(/-advertising$/, "")
    .replace(/-ads$/, "");
  const [loading, setLoading] = useState(true);
  const [ads, setAds] = useState([]);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("name");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pages: 1,
    limit: 12,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  // const observer = useRef();
  // const lastAdElementRef = useCallback(node => {
  //   if (isLoading) return;

  //   if (observer.current) observer.current.disconnect();

  //   observer.current = new IntersectionObserver(entries => {
  //     if (entries[0].isIntersecting && hasMore) {
  //       setPagination(prev => ({ ...prev, page: prev.page + 1 }));
  //     }
  //   });

  //   if (node) observer.current.observe(node);
  // }, [isLoading, hasMore]);

  useEffect(() => {
    const fetchAds = async () => {
      if (pagination.page === 1) {
        setLoading(true);
      }
      setIsLoading(true);
      setError(null);

      try {
        let url;
        const useSearchEndpoint = category === "radio" || category === "transit";

        switch (category) {
          case "radio":
            url = `/api/radio-ads/search`;
            break;
          case "cinema":
            url = `/api/cinema-ads`;
            break;
          case "transit":
            url = `/api/transit-ads/search`;
            break;
          case "outdoor":
            url = `/api/outdoor-categories`;
            break;
          case "non-traditional":
            url = `/api/non-traditional-ads`;
            break;
          case "btl":
            url = `/api/btl-ads`;
            break;
          case "digital":
            url = `/api/digital-ads`;
            break;
          case "newspaper":
            url = `/api/newspaper-ads`;
            break;
          default:
            throw new Error("Invalid category");
        }

        // Build query parameters
        const params = new URLSearchParams();
        params.append('page', pagination.page);
        params.append('limit', pagination.limit);

        if (!useSearchEndpoint) {
          params.append('where[and][0][enabled][not_equals]', 'false');
        }

        // Add filters as query parameters for search endpoints
        if (useSearchEndpoint) {
          Object.entries(selectedFilters).forEach(([key, value]) => {
            if (value && value !== "All") {
              // Handle array values
              if (Array.isArray(value)) {
                value.forEach(v => {
                  if (key === 'locations') {
                    params.append('location', v);
                  } else {
                    params.append(key, v);
                  }
                });
              } else {
                // Handle single values
                if (key === 'locations') {
                  params.append('location', value);
                } else {
                  params.append(key, value);
                }
              }
            }
          });

          // Add sorting
          if (sortBy && sortBy !== 'popularity') {
            let sortParam = '';
            switch (sortBy) {
              case 'name':
                sortParam = 'title';
                break;
              case 'price_low':
                sortParam = 'minimum_billing_amount';
                break;
              case 'price_high':
                sortParam = '-minimum_billing_amount';
                break;
              default:
                sortParam = sortBy;
            }
            if (sortParam) {
              params.append('sort', sortParam);
            }
          }
        }

        url += `?${params.toString()}`;

        const response = await axios.get(url);
        const data = response.data;

        if (response.status === 200) {
          const items = (data.docs || []).filter((ad) => ad.enabled !== false);
          setAds((prevAds) =>
            pagination.page === 1 ? items : [...prevAds, ...items]
          );
          setPagination({
            total: data.totalDocs,
            page: data.page,
            pages: data.totalPages,
            limit: data.limit,
          });
          setHasMore(data.page < data.totalPages);
        } else {
          setError(data.message || "Failed to fetch ads");
        }
      } catch (error) {
        console.error("Error fetching ads:", error);
        setError("Failed to fetch ads. Please try again later.");
      } finally {
        setLoading(false);
        setIsLoading(false);
      }
    };

    fetchAds();
  }, [category, pagination.page, pagination.limit, selectedFilters, sortBy]);

  const sortOptions = [
    { value: "name", label: "Sort by Name" },
    { value: "price_low", label: "Price: Low to High" },
    { value: "price_high", label: "Price: High to Low" },
  ];

  const handleSortClick = (value) => {
    setSortBy(value);
    setShowSortDropdown(false);
  };

const getFilterOptions = () => {
    switch (category) {
      case "radio":
        return {
          language: ["All", ...new Set(ads?.map((ad) => ad.language).filter(Boolean) || [])],
          frequency: ["All", ...new Set(ads?.map((ad) => ad.frequency).filter(Boolean) || [])],
          rank: ["All", ...new Set(ads?.map((ad) => ad.rank).filter(Boolean) || [])],
          station: ["All", ...new Set(ads?.map((ad) => ad.station).filter(Boolean) || [])],
          tier: ["All", "tier1", "tier2", "tier3", "rural"],
          audience: ["All", ...new Set(ads?.flatMap((ad) => ad.audience || []).filter(Boolean) || [])],
          locations: [
            "All",
            ...new Set(
              ads?.flatMap(
                (ad) =>
                  ad.locations?.map(
                    (loc) => loc.city || loc.state || loc.country
                  ) || []
              )
            ),
          ],
        };

      case "cinema":
        return {
          theater: ["All", ...new Set(ads?.map((ad) => ad.theater) || [])],
          screen_type: ["All", "regular", "premium", "imax"],
          tier: ["All", "premium", "standard", "basic"],
          locations: [
            "All",
            ...new Set(
              ads?.flatMap(
                (ad) =>
                  ad.locations?.map(
                    (loc) => loc.city || loc.state || loc.country
                  ) || []
              )
            ),
          ],
        };

      case "transit":
        return {
          mediaType: ["All", "metro_train", "bus", "auto", "cab"],
          tier: ["All", "tier1", "tier2", "tier3", "rural"],
          locations: [
            "All",
            ...new Set(
              ads?.flatMap(
                (ad) =>
                  ad.locations?.map(
                    (loc) => loc.city || loc.state || loc.country
                  ) || []
              )
            ),
          ],
        };

      case "outdoor":
        // Outdoor categories are simple navigation cards - no filters
        return {};

      case "non-traditional":
        return {
          class: [
            "All",
            "ambient",
            "guerrilla",
            "experiential",
            "installation",
          ],
          tier: ["All", "premium", "standard", "basic"],
          locations: [
            "All",
            ...new Set(
              ads?.flatMap(
                (ad) =>
                  ad.locations?.map(
                    (loc) => loc.city || loc.state || loc.country
                  ) || []
              )
            ),
          ],
        };

      case "btl":
        return {
          activity_type: [
            "All",
            "event",
            "activation",
            "sampling",
            "exhibition",
            "road_show",
          ],
          tier: ["All", "premium", "standard", "basic"],
          locations: [
            "All",
            ...new Set(
              ads?.flatMap(
                (ad) =>
                  ad.locations?.map(
                    (loc) => loc.city || loc.state || loc.country
                  ) || []
              )
            ),
          ],
        };

      case "digital":
        return {
          platform: [
            "All",
            "social_media",
            "search_engine",
            "display_network",
            "email",
            "content_marketing",
          ],
          tier: ["All", "premium", "standard", "basic"],
          locations: [
            "All",
            ...new Set(
              ads?.flatMap(
                (ad) =>
                  ad.locations?.map(
                    (loc) => loc.city || loc.state || loc.country
                  ) || []
              )
            ),
          ],
        };

      case "newspaper":
        return {
          languages: ["All", ...new Set(ads?.map((ad) => ad.languages) || [])],
          locations: [
            "All",
            ...new Set(
              ads?.flatMap(
                (ad) =>
                  ad.locations?.map(
                    (loc) => loc.city || loc.state || loc.country
                  ) || []
              )
            ),
          ],
        };

      default:
        return {};
    }
  };

  const handleFilterChange = (filterType, value) => {
    // Handle both single values and arrays
    const filterValue = Array.isArray(value)
      ? (value.length === 0 ? null : value)
      : (value === "All" ? null : value);

    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: filterValue,
    }));
    // Reset to page 1 when filters change
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Reset pagination when category changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSelectedFilters({});
  }, [category]);

  const applyFilters = (items) => {
    if (!items) return [];

    return items.filter((item) => {
      return Object.entries(selectedFilters).every(
        ([filterType, filterValue]) => {
          if (!filterValue) return true;

          // Helper function to check if value matches (handles both single values and arrays)
          const matchesFilter = (itemValue, filterValue) => {
            if (Array.isArray(filterValue)) {
              return filterValue.some(fv => {
                if (Array.isArray(itemValue)) {
                  return itemValue.includes(fv);
                }
                return itemValue === fv;
              });
            }
            if (Array.isArray(itemValue)) {
              return itemValue.includes(filterValue);
            }
            return itemValue === filterValue;
          };

          switch (filterType) {
            case "language":
              return matchesFilter(item.language, filterValue);
            case "frequency":
              return matchesFilter(item.frequency, filterValue);
            case "rank":
              return matchesFilter(item.rank, filterValue);
            case "station":
              return matchesFilter(item.station, filterValue);
            case "audience":
              return matchesFilter(item.audience, filterValue);
            case "theater":
              return matchesFilter(item.theater, filterValue);
            case "screen_type":
              return matchesFilter(item.screen_type, filterValue);
            case "mediaType":
              return matchesFilter(item.mediaType, filterValue);
            case "vehicle_type":
              return matchesFilter(item.vehicle_type, filterValue);
            case "type":
              return matchesFilter(item.type, filterValue);
            case "category":
              return matchesFilter(item.category, filterValue);
            case "class":
              return matchesFilter(item.class, filterValue);
            case "activity_type":
              return matchesFilter(item.activity_type, filterValue);
            case "platform":
              return matchesFilter(item.platform, filterValue);
            case "tier":
              return matchesFilter(item.tier, filterValue);
            case "locations":
              if (Array.isArray(filterValue)) {
                return filterValue.some((fv) =>
                  (item.locations || []).some(
                    (loc) =>
                      loc.city === fv ||
                      loc.state === fv ||
                      loc.country === fv
                  )
                );
              }
              return (item.locations || []).some(
                (loc) =>
                  loc.city === filterValue ||
                  loc.state === filterValue ||
                  loc.country === filterValue
              );
            default:
              return true;
          }
        }
      );
    });
  };

  const applySorting = (items) => {
    if (!items) return [];

    return [...items].sort((a, b) => {
      switch (sortBy) {
        case "name":
          return (a.title || "").localeCompare(b.title || "");
        case "price_low":
          return (
            (a.minimum_billing_amount || 0) - (b.minimum_billing_amount || 0)
          );
        case "price_high":
          return (
            (b.minimum_billing_amount || 0) - (a.minimum_billing_amount || 0)
          );
        case "popularity":
        default:
          return (b.popularity || 0) - (a.popularity || 0);
      }
    });
  };

  // For radio and transit, filtering/sorting is done server-side
  // For other categories, we still do client-side filtering
  const useServerSideFiltering = category === "radio" || category === "transit";
  const displayAds = useServerSideFiltering
    ? ads
    : applySorting(applyFilters(ads));
  const totalResults = useServerSideFiltering
    ? pagination.total
    : displayAds.length;
  const canLoadMore = useServerSideFiltering && hasMore;

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Error</h2>
        <p className="text-gray-600 mb-6">{error}</p>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      {category === "digital" && <MetaAdLibrarySection />}
      <div className="min-h-screen bg-gray-50">
        <AdvertisingHeader
          category={category}
          sortOptions={sortOptions}
          sortBy={sortBy}
          handleSortClick={handleSortClick}
          showSortDropdown={showSortDropdown}
          setShowSortDropdown={setShowSortDropdown}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          filterOptions={getFilterOptions()}
        />
        <div className="container mx-auto px-4 py-4">
          <p className="text-gray-600 mb-6">
            Showing {displayAds.length}
            {useServerSideFiltering ? ` of ${totalResults}` : ""} results
          </p>

          {ads.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No ads found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {displayAds.map((ad) => (
                <div key={ad._id}>
                  <Card
                    data={{
                      ...ad,
                      price: ad.minimum_billing_amount,
                      hasChildren: ad.options?.length > 0,
                      parent: null,
                    }}
                    category={category}
                    options={true}
                  />
                </div>
              ))}
            </div>
          )}

          {canLoadMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                }
                className="flex items-center px-6 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
              >
                Load More
                <FaArrowDown className="ml-2" />
              </button>
            </div>
          )}

          {isLoading && !loading && (
            <div className="flex justify-center mt-8">
              <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>

        <Clients category="advertising" />
        <Videos category={category} />
        <div className="py-12">
          <ClientSection />
        </div>
      </div>
      <ReachOutSection />
      <Footer />
    </>
  );
};

export default AdsPage;
